import { User } from 'oidc-client-ts';
import ICustomerData, {
  Amount,
  ICancelCustomerBookingResponse,
  ICustomerAudit, ICustomerBooking,
  ICustomerBookingResponse,
  ICustomerTripResponse,
  ICustomerTripsResponse,
  IdVerificationSession,
  IRefundCustomerTripPaymentResponse,
  ITripsPaymentsResponse
} from "../../models/customer-data.interface";
import { getAuthConfig } from "../../utils/auth-utils";
import { IComment } from "../../models/comments-data.interface";
import {SearchType} from "../../redux/slices/customer-slice";

function getAccessToken(): string {
  const authConfig = getAuthConfig();
  const oidcStorage = sessionStorage.getItem(`oidc.user:${authConfig.authority}:${authConfig.client_id}`)
  if (!oidcStorage) {
    return 'No token';
  }

  return User.fromStorageString(oidcStorage)?.access_token;
}

function headers(): object {
  const headers = {
    Authorization: `Bearer ${getAccessToken()}`,
  }

  if (process.env.REACT_APP_BACKEND_URL.includes('localhost')) {
    headers['user-agent-email'] = 'admin@vwfs.io';
    headers['user-agent-token-type'] = 'EMG'
  }

  return headers;
}

export async function getCustomerByEmailOrDriverId(searchString: string, type: SearchType): Promise<ICustomerData[]> {
  const encodedSearchString = encodeURIComponent(searchString);
  const query = type === 'EMAIL' ? `email=${encodedSearchString}` : `driverId=${encodedSearchString}`;
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/v1/customers?${query}`, {
    headers: { ...headers() }
  }
  );

  if (response.status === 403 || response.status === 401) {
    throw new Error('FORBIDDEN')
  }

  const responseBody = await response.json();
  return responseBody.data;
}

export async function getCustomerById(id: string): Promise<ICustomerData> {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/v1/customers/${id}`, {
    headers: { ...headers() },
  }
  );
  const responseBody = await response.json();
  return responseBody.data;
}

export async function getCustomerIdVerificationSessions(id: string): Promise<IdVerificationSession[]> {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/v1/customers/${id}/id-verification-sessions`, {
    headers: { ...headers() }
  }
  );
  const responseBody = await response.json();

  return responseBody.data;
}

export async function getCustomerAuditTrail(id: string, pageSize: number, cursor?: string): Promise<ICustomerAudit> {
  const cursorParam = cursor ? '&cursor=' + cursor : '';
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/v1/customers/${id}/audit-trail?pageSize=${pageSize}${cursorParam}`, {
    headers: { ...headers() }
  }
  );
  return await response.json();
}

export async function markCustomerForDeletion(id: string): Promise<string> {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/v1/customers/${id}/mark-for-deletion`, {
    headers: { ...headers() },
    method: "POST"
  },
  );
  if (response?.status === 204) {
    return await response.text();
  } else {
    return (await response.json())?.errors[0]?.code as string | 'UNKNOWN_ERROR';
  }
}

export async function unmarkCustomerForDeletion(id: string): Promise<string> {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/v1/customers/${id}/unmark-for-deletion`, {
    headers: { ...headers() },
    method: "POST"
  },
  );

  return await response.text();
}

export async function resetIdVerification(id: string): Promise<string> {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/v1/customers/${id}/reset-id-verification`, {
    headers: { ...headers() },
    method: "POST"
  },
  );
  return await response.text();
}

export async function suspendCustomer(id: string): Promise<string> {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/v1/customers/${id}/suspend`, {
    headers: { ...headers() },
    method: "POST"
  },
  );
  if (response.status === 204) {
    return await response.text();
  } else {
    const json = await response.json();
    return json.errors[0].code as string | 'UNKNOWN_ERROR';
  }
}

export async function unsuspendCustomer(id: string): Promise<string> {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/v1/customers/${id}/unsuspend`, {
    headers: { ...headers() },
    method: "POST"
  },
  );
  return await response.text();
}

export async function getCustomerBookings(id: string): Promise<ICustomerBookingResponse> {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/v2/customers/${id}/bookings`, {
    headers: { ...headers() }
  }
  );
  return await response.json();
}

export async function getCustomerTrips(id: string): Promise<ICustomerTripsResponse> {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/v2/customers/${id}/trips`, {
    headers: { ...headers() }
  }
  );
  return await response.json();
}

export async function getCustomerTrip(customerId: string, tripId: string): Promise<ICustomerTripResponse> {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/v2/customers/${customerId}/trips/${tripId}`, {
    headers: { ...headers() }
  }
  );
  return await response.json();
}

export async function getTripPayments(state: string, cursor?: string, dateFrom?: string, dateTo?: string, msp?: string): Promise<ITripsPaymentsResponse> {
  const params = new URLSearchParams();
  params.append("state", state);
  params.append('pageSize', '10');

  if (cursor) {
    params.append("cursor", cursor);
  }

  if (dateFrom && dateTo) {
    params.append("to", dateTo);
    params.append("from", dateFrom);
  }

  if (msp) {
    params.append('serviceProviderId', msp);
  }

  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/v1/trippayments?${params.toString()}`, {
    headers: { ...headers() },

  }
  );
  return await response.json();
}

export async function cancelCustomerBooking(customerId: string, bookingId: string, externalBookingId: string, userInput: string): Promise<ICancelCustomerBookingResponse> {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/v1/bookings/${bookingId}/cancel`, {
    headers: { ...headers() },
    method: "POST",
    body: JSON.stringify({
      ...(customerId !== 'guest' && { customerId: customerId }),
      ...(customerId === 'guest' && { externalBookingId: externalBookingId }),
      ...(customerId === 'guest' && { userInput: userInput }),
    })
  }
  );

  if (response?.status === 200) {
    return await response.json();
  } else {
    throw Error((await response.json())?.errors[0]?.code as string | 'UNKNOWN_ERROR');
  }
}

export async function refundCustomerBooking(customerId: string, bookingId: string): Promise<ICancelCustomerBookingResponse> {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/v1/customers/${customerId}/bookings/${bookingId}/refund`, {
    headers: { ...headers() },
    method: "POST"
  }
  );

  if (response?.status === 200) {
    return await response.json();
  } else {
    throw Error((await response.json())?.errors[0]?.code as string | 'UNKNOWN_ERROR');
  }
}

export async function refundCustomerTrip(trippaymentId: string, serviceProviderId: string, refundAmount: Amount): Promise<IRefundCustomerTripPaymentResponse> {
  const payloadString = JSON.stringify({
    serviceProviderId: serviceProviderId,
    refundAmount: refundAmount
  });

  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/v1/trippayments/${trippaymentId}/refund`, {
    headers: {
      ...headers(),
      "Content-Type": "application/json"
    },
    method: "POST",
    body: payloadString
  });

  if (response?.status === 200) {
    return await response.json();
  } else {
    throw Error((await response.json())?.errors[0]?.code as string | 'UNKNOWN_ERROR');
  }
}

export async function getBookingByIdAndUserInput(bookingId: string, userInput: string): Promise<ICustomerBooking> {
  const encodedBookingId = encodeURIComponent(bookingId);
  const encodedUserinput = encodeURIComponent(userInput);
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/v1/bookings?externalBookingId=${encodedBookingId}&userInput=${encodedUserinput}`, {
    headers: { ...headers() }
  }
  );

  if (response.status === 403 || response.status === 401) {
    throw new Error('FORBIDDEN')
  }

  const responseBody = await response.json();
  return responseBody.data;
}

export async function getCustomerComments(id: string): Promise<IComment[]> {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/v1/customers/${id}/comments`, {
    headers: { ...headers() },
  }
  );
  const responseBody = await response.json();
  return responseBody.data?.sort((e1, e2) => { return new Date(e2.updatedAt).getTime() - new Date(e1.updatedAt).getTime() });
}

export async function addCustomerComment(
  customerId: string,
  summary: string,
  description: string,
): Promise<IComment> {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/v1/customers/${customerId}/comments`, {
    headers: { ...headers() },
    method: "POST",
    body: JSON.stringify({
      creatorType: 'AGENT',
      summary,
      description,
    })
  }
  );

  if (response?.status === 200) {
    return await response.json();
  } else {
    throw Error((await response.json())?.errors[0]?.code as string | 'UNKNOWN_ERROR');
  }
}
